import api, { ApiResponse, extractApiError } from './api';
import type { AiMeasureTone, HazardCategory, OrganizationCategory, OrganizationType, SiteStatus, ApprovalLineMetaRow, AssignmentSource, ApproverDirectoryEntry, SiteApprovalLineMetaData, SiteAssignmentType, VerificationStatus, VerificationRejectionReason, VerificationDocumentType } from '@safetyhub/shared';
import { VERIFICATION_REJECTION_REASON_LABELS } from '@safetyhub/shared';
import {
  memberKindOf,
  MEMBER_KIND_LABELS,
  OWNER_LABEL,
  MEMBER_LABEL,
  OWNER_ROLE_KEY,
  type MemberKind,
  type CreateSiteRequestedResponse,
  type SiteOperatorGroupField,
  type SiteInfoManageField,
} from '@safetyhub/shared';
import type { BadgeTone } from '@safetyhub/ui';
export type { ApprovalLineMetaRow, ApproverDirectoryEntry, AssignmentSource, SiteApprovalLineMetaData };
import type { Company, CreateCompanyDto } from './companies';
import { useAuthStore } from '../stores/auth';
import type { Group } from './groups';
export type { Group } from './groups';

/**
 * 조직 역할의 표시 축(company-rbac P7-b-2, 2026-09-25) — 판정 정본은 **shared `constants/member-kind.ts`** 다
 * (종전엔 web과 admin이 각자 같은 판정을 들고 있어 두 화면이 갈릴 수 있었다). 여기서는 기존 import 경로를
 * 유지하기 위해 다시 내보낸다 — 이 모듈이 web의 조직 파사드이고, 소비처 15곳과 이 모듈을 `vi.mock`하는
 * spec 24개가 그 경로를 쓴다.
 *
 * shared로 가지 않은 것 둘: **외부 인원 문구**(아래 — admin은 「외부 연락처」라 부른다, 제품 결정 전까지 각자)와
 * **배지 tone**(`BadgeTone`이 `@safetyhub/ui` 타입이라 shared가 의존할 수 없다).
 */
// ⚠️ 이 모듈 안에서도 쓰이므로(초대 배지·역할 배지) 다시 내보내기만 하지 않고 **import해서 함께 내보낸다** —
// `export … from`은 지역 스코프에 들이지 않는다.
// `OWNER_ROLE_KEY`는 판정용이 아니라 **보내는 값**이다(판정은 `isOwner` 성질 — shared doc).
export { memberKindOf, MEMBER_KIND_LABELS, OWNER_LABEL, MEMBER_LABEL, OWNER_ROLE_KEY };
export type { MemberKind };

/** 외부 인원(배정 0) 라벨 — **web의 문구**다. admin은 같은 종류를 「외부 연락처」라 부른다. */
export const EXTERNAL_LABEL = '외부 인원';

/**
 * 기관 자격 인증의 세 축 — **정본은 전부 `@safetyhub/shared`**이고 여기는 별칭이다(2026-10-20).
 *
 * ## 왜 별칭인가 — 종전엔 세 벌을 **재선언**하고 있었고 이미 갈라져 있었다
 * `VerificationStatus`가 shared 9개 ↔ 여기 8개로 **`REVIEW_AMBIGUOUS`가 빠져 있었고**,
 * `VerificationRejectionReason`은 shared 8개(활성 5 + legacy 3) ↔ 여기 **활성 5개만**이었다.
 * 서버는 `REVIEW_AMBIGUOUS`를 실제로 설정·분기하고(`admin/admin-verification.service.ts`) 조직
 * 응답은 그 칼럼을 **그대로** 내려주므로, **서버가 보내는 값을 화면 타입이 표현할 수 없는 상태**였다.
 *
 * ⚠️ **그 어긋남이 조용했던 이유를 적어 둔다 — 방향이 하나다.** 아래 `REJECTION_REASON_LABELS`가
 * 넓은 Record(shared 8키 객체)를 **좁은 Record 타입**에 대입하는 형태라, shared가 값을 **더해도**
 * tsc가 아무 말을 하지 않았다(제거할 때만 깨진다). 즉 drift가 **쌓이는 방향으로만 침묵**했다.
 * 별칭으로 바꾸면 양쪽이 같은 union이라 그 침묵이 사라진다.
 * ⚠️ 그리고 web은 **Vite(esbuild) 빌드라 CI가 타입을 보지 않는다** — 이 축은 로컬 tsc만 잡는다.
 *
 * ## 선례
 * 이 파일의 {@link AssignmentType}(shared `SiteAssignmentType`의 별칭)과 맨 아래
 * 「Re-export from @safetyhub/shared for backwards compatibility」 블록이 **같은 이유의 같은 형태**다.
 * 소비처(`pages/settings/Organization.tsx`·`components/VerificationPendingBanner.tsx`)가
 * `./organizations`에서 이 이름들을 가져가므로 경로를 바꾸지 않고 별칭으로 둔다.
 *
 * ⚠️ **여기에 멤버를 손으로 다시 적지 말 것** — 늘릴 곳은 shared의 그 union 하나다.
 *
 * ## 무엇이 바뀌었고 **무엇이 그대로인가** (아래 {@link MANUAL_REVIEW_VERIFICATION_STATUSES})
 * ⚠️ **종전에 이 자리에 census 표가 하나 더 있었고 세 칸이 낡아 결론이 거짓이었다**(2026-10-21에
 * 걷었다). 그 표는 「운영 `switch`/`Record<VerificationStatus,…>` **0건** ⇒ 멤버를 늘리면
 * **아무것도 red가 되지 않는다**」로 끝났는데, 그 표를 적은 뒤 같은 라운드가 `Record`를 **둘**
 * 만들어 결론이 그 자리에서 뒤집혔다(실측은 아래 표의 각주). 그리고 바로 위 두 단락이
 * 「현재형 서술이 하루도 못 가 낡았다」를 **이미 적고 있었는데 같은 축으로 또 났다** —
 * ⇒ 처방은 서술을 고치는 것이 아니라 **표를 두 벌 두지 않는 것**이다. 아래 한 표가 정본이고,
 * 「무엇이 red가 되나」는 수를 적는 대신 **그 자리를 심볼로 가리킨다**(수는 낡고 심볼은 안 낡는다).
 *
 * | | 상태 |
 * |---|---|
 * | `REVIEW_AMBIGUOUS` 오표시 | **닫혔다** — 두 화면이 공용 술어를 읽는다 |
 * | **수동 심사 축**의 새 멤버 누락 | **신호가 생겼다** — `__tests__/verificationStatus.test.ts`의 `Record<VerificationStatus, boolean>`이 **tsc로 미분류를 거부**한다(그 축 한정) |
 * | **표시 축**의 새 멤버 누락 | **신호가 생겼다**(2026-10-20 후속) — 같은 spec의 `Record<VerificationStatus, VerificationBand>`가 거부한다. 분류 자리는 {@link verificationBand} **하나**다 |
 * | `settings/Organization.tsx`의 **폴백 방향** | **중립으로 옮겼다**(2026-10-20 후속) — `REJECTED`가 명시 분기가 되고 폴백이 `'unknown'`이다 |
 * | 배너 `displayMode` 사슬 | **신호가 생겼다** — {@link VERIFICATION_DISPLAY_MODE}가 `Record<VerificationStatus, …>`라 tsc가 미기재를 거부한다 |
 * | **서류 제출 창구**의 새 멤버 누락 | **신호가 생겼다**(2026-10-21) — {@link VERIFICATION_UPLOAD_SURFACE}가 같은 형태다. 그 전까지 넷이 `return null`로 떨어져 **업로드 컨트롤이 0개**였다(그 JSDoc이 경위의 정본) |
 * | 그 밖의 단일 상태 분기(본문 `<p>` 게이트 등) | 🔴 **그대로 0건** — 새 멤버는 조용히 빠진다(밴드가 중립이라 **오표시는 아니다**) |
 *
 * ⚠️ **「신호가 생겼다」를 좁게 읽을 것** — 닫힌 것은 「새 멤버가 **반려로 보이거나 컨트롤을 잃는
 * 것**」이고 「새 멤버에 **맞는 문구가 나오는 것**」이 아니다. 폴백은 「인증 상태를 확인하고
 * 있습니다」라 말하므로 **거짓은 아니지만 아무것도 안내하지 못한다.**
 *
 * ⚠️ **그리고 그 tsc 강제 넷은 CI에서 돌지 않는다** — web은 CI가 Vite(esbuild)로 빌드해 타입을
 * 보지 않고, web tsc는 shared를 `dist/index.d.ts`로 해소하므로 그 red는 **shared를 빌드한 뒤 로컬
 * tsc를 돌릴 때만** 보인다. CI에서 이 축을 잡는 것은 `__tests__/verificationStatus.test.ts`의
 * **소스 스캔 래칫**(shared src 텍스트를 직접 읽어 멤버 집합을 대조한다)이다.
 *
 * ⇒ **새 멤버를 더하는 사람은** 네 Record가 red가 되는 것을 **신호로 받고**, 위 표의 마지막
 * 줄(본문 `<p>` 게이트)을 **손으로** 함께 보라.
 *
 * ⚠️ **운영 코드에서 이 union을 `===`로 비교하는 자리는 여전히 래칫 밖이다.** 실측(2026-10-21 ·
 * 파일 루프 + 주석·import 제거, 분모 운영 509파일): **12자리 / 2파일** —
 * `pages/settings/Organization.tsx` 11(`===` 10 + `!==` 1) · `lib/educationPeriodEligibility.ts` 1.
 * 배너는 **0**이다(이 라운드 전에 `verificationDisplayMode` 호출로 교체됐다).
 * ⚠️ needle에 `verificationStatus ===`를 쓰면 **다른 축을 삼킨다** — `hooks/useSiteFormState.ts`의
 * 2자리는 `CompanyVerificationStatus`(NTS 진위확인, `FULLY_VERIFIED`)로 **이 union이 아니다**.
 * 그리고 **`!==`를 빼면 과소**가 된다(그 1자리가 빠져 11이 10으로 보인다).
 */
export type { VerificationStatus, VerificationRejectionReason, VerificationDocumentType };

/**
 * **수동 심사 대역 상태** — 자동 검증이 판정을 못 내려 **사람이 심사해야 하는** 상태 집합.
 *
 * ## 왜 공용 술어인가 — 두 화면이 각자 손열거해서 갈렸다 (2026-10-20)
 * 종전엔 같은 축을 **두 자리가 따로** 열거했고 **이미 어긋나 있었다**:
 * - `pages/settings/Organization.tsx`의 지역 변수 `_isManualReview` — `REVIEW_AMBIGUOUS`가 **빠져 있었다**
 * - `components/VerificationPendingBanner.tsx` — `REVIEW_AMBIGUOUS`를 `REVIEW_NO_MATCH`와 **같게 처분**
 *
 * 그래서 그 상태의 조직에게 설정 화면은 **「반려 — 사유 미제공」**(rose)을 보여 주고 배너는
 * 「기관 인증 심사 대기 중」(amber)을 보여 주는 상태였다. 서버는 그 값을 **실제로 쓴다** —
 * `organization-verification.service.ts`가 공시 Top2 유사도 격차가 margin 미만이면
 * `REVIEW_AMBIGUOUS`로 적는다(후보 선택 필요). 즉 **수동 심사 대기를 반려로 보이는** 것이었다.
 *
 * ⚠️ **기제는 「한쪽이 틀렸다」가 아니라 「같은 축을 두 곳이 손열거한다」**다. 한쪽만 고치면
 * 다음 멤버가 생길 때 **또 갈린다** — 이번이 그 실례다. 그래서 집합을 여기로 올리고 두 화면이
 * 이것을 읽는다. 늘릴 곳은 **이 상수 하나**다.
 *
 * ⚠️ **지역 변수는 spec이 볼 수 없다** — 그래서 종전엔 이 축을 재는 검사가 **원리적으로 0개**였다.
 * 지금은 `__tests__/verificationStatus.test.ts`가 **shared union 전수**에 대해 분류를 못박는다
 * (분모를 `Record<VerificationStatus, …>`로 파생시켜, shared에 멤버가 늘면 **tsc가 미분류를 거부**한다).
 *
 * `satisfies`로 원소가 union 안에 있음을 강제한다 — 오타(`REVIEW_AMBIGOUS`)가 조용히 「아무것도
 * 매치하지 않는 문자열」이 되는 것을 막는다.
 */
export const MANUAL_REVIEW_VERIFICATION_STATUSES = [
  'REVIEW_TIMEOUT',
  'REVIEW_NO_MATCH',
  'REVIEW_AMBIGUOUS',
  'REVIEW_REAPPLIED',
] as const satisfies readonly VerificationStatus[];

/**
 * 그 상태인가 — **입력을 `string | undefined | null`로 받는다.** 호출부 중 하나
 * (`VerificationPendingBanner`)가 읽는 `company.agencyVerificationStatus`는 `auth.ts`에서
 * `string | null`로 선언돼 있어, `VerificationStatus`만 받으면 호출부가 캐스트를 끼워 넣게 되고
 * 그 캐스트가 **다음 드리프트의 입구**가 된다(그 느슨한 타입 자체는 별 작업이다).
 */
export function isManualReviewVerificationStatus(
  status: VerificationStatus | string | undefined | null,
): boolean {
  return (MANUAL_REVIEW_VERIFICATION_STATUSES as readonly string[]).includes(status ?? '');
}

/**
 * 인증 상태의 **표시 밴드** — `'none'`은 렌더하지 않는다는 뜻이다.
 *
 * 밴드는 **container·아이콘**만 정한다. 제목·본문은 {@link verificationBand}의 JSDoc 참조.
 */
export type VerificationBand = 'none' | 'progress' | 'attention' | 'rejected' | 'unknown';

/**
 * 상태 → 표시 밴드.
 *
 * ## 왜 이 함수가 생겼나 — **폴백이 「반려」였다** (2026-10-20)
 * 종전 `pages/settings/Organization.tsx`는 색·아이콘·제목 **세 사슬 모두**를
 * `_isReviewBand ? amber : PENDING ? amber : rose`로 적었고 **`REJECTED`가 명시 분기가 아니라
 * 폴백**이었다. 그래서 shared union에 멤버가 늘면 그 상태가 **「반려 — 사유 미제공」**(rose + X)로
 * 보였고, 설명문이 전부 명시 상태에 걸려 있어 **무슨 일인지 말해 주는 문장이 0줄**이었다.
 * `REVIEW_AMBIGUOUS`에서 실제로 일어난 일이 그것이다(서버는 공시 Top2 격차가 margin 미만일 때
 * 그 값을 **실제로 쓴다** — `organization-verification.service.ts`).
 *
 * ⚠️ **기제는 「한쪽이 틀렸다」가 아니다** — 형제 `VerificationPendingBanner`도 **똑같이** 한
 * 상태를 폴백에 맡겼는데 맡긴 것이 `PENDING`이었다. 두 화면이 같은 구조를 고르고 **암묵으로 둔
 * 멤버만 달랐고**, 그래서 모르는 상태가 한쪽에선 「진행 중」·한쪽에선 「반려」가 됐다.
 *
 * ⇒ 그래서 **`'unknown'`을 값으로 둔다.** 근거는 **오류 방향의 비대칭**이다 — 「진행 중」 오표시는
 * 사용자가 다시 보면 끝이고 「사유 없이 반려됐다」는 문의·항의·포기를 만든다. 그리고 `REJECTED`는
 * **명시할 수 있는 값**이므로 폴백을 옮겨도 **실제 반려의 표시는 1비트도 잃지 않는다.**
 *
 * ## 이 함수가 소유하지 **않는** 것
 * `'attention'` 안에서 `INFO_MISMATCH`(사용자가 정정)와 수동 심사 넷은 **같은 색·아이콘이지만
 * 다른 문구**다. 그 가름은 호출부의 {@link isManualReviewVerificationStatus} /
 * `=== 'INFO_MISMATCH'`가 계속 맡는다 — ⚠️ **둘을 한 술어로 합치지 말 것.**
 *
 * ## union 밖 문자열은 `'none'`이 아니라 `'unknown'`이다
 * **서버가 먼저 새 상태를 쓰고 web 타입이 낡는 것이 실제 순서**이므로(그 값은 `stores/auth`에서
 * `string | null`로 온다) 그때 배너가 **사라지면** 안 된다. `'none'`은 「인증이 필요 없거나 끝났다」
 * 둘뿐이다 — 종전 상위 게이트의 `!== 'NOT_REQUIRED' && !== 'VERIFIED'` 손열거를 이 값이 대신한다
 * (그 게이트도 같은 드리프트 입구였다).
 *
 * 분류의 정본 검사는 `__tests__/verificationStatus.test.ts`의
 * `Record<VerificationStatus, VerificationBand>`다 — shared에 멤버가 늘면 **tsc가 미기재를 거부**한다.
 */
export function verificationBand(
  status: VerificationStatus | string | undefined | null,
): VerificationBand {
  if (!status || status === 'NOT_REQUIRED' || status === 'VERIFIED') return 'none';
  if (status === 'PENDING') return 'progress';
  if (status === 'INFO_MISMATCH' || isManualReviewVerificationStatus(status)) return 'attention';
  if (status === 'REJECTED') return 'rejected';
  return 'unknown';
}

/**
 * 배너의 **문구 모드** — `'none'`은 렌더하지 않는다는 뜻이다.
 *
 * ⚠️ 이것은 {@link VerificationBand}보다 **잘다**(모드 9 ↔ 밴드 5). 그래서 밴드 래칫이 이 축을
 * 덮지 못하고 {@link verificationDisplayMode}가 자기 래칫을 가진다 — 사유는 그 JSDoc.
 */
export type VerificationDisplayMode =
  | 'none'
  | 'pending-retrying'
  | 'pending-info-mismatch'
  | 'pending-review-timeout'
  | 'pending-review-timeout-submitted'
  | 'pending-review-no-match'
  | 'pending-review-no-match-submitted'
  | 'pending-review-ambiguous'
  | 'pending-review-reapplied'
  | 'rejected'
  | 'unknown';

/** 서류 제출 여부로 문구가 갈리는 상태의 값. */
type DocsSplit = (hasRequiredDocs: boolean) => VerificationDisplayMode;

/**
 * 상태 → 문구 모드. **`Record`라 shared에 멤버가 늘면 tsc가 미기재를 거부한다.**
 *
 * ## 왜 밴드 래칫으로 부족한가 — 문구 축이 밴드보다 «잘다»
 * `settings/Organization.tsx`의 **제목·본문**은 문구를 밴드 + 공용 술어 + `isWorkplace` + 서류
 * 유무에서만 파생하므로, 그 축에 한해 {@link verificationBand}의 Record가 끝까지 덮는다.
 * ⚠️ **종전에 이 자리가 「그 화면에는 개별 상태를 손열거하는 자리가 **없다**」라 적고 있었고 그것은
 * 거짓이었다** — 실측 **11자리**이고(2026-10-21, 윗 블록의 census 각주) 그 손열거가 **서류 제출
 * 창구**를 정하고 있어 수동 심사 넷 중 둘이 막다른 길이었다. 그 축의 소유자는 이제
 * {@link VERIFICATION_UPLOAD_SURFACE}다. 「그 Record가 그 화면을 **끝까지** 덮는다」로 읽지 말 것 —
 * 덮는 것은 **제목·본문 문구**이고 창구·게이트는 다른 Record가 맡는다.
 * **배너는 또 다르다** — `REVIEW_TIMEOUT`과 `REVIEW_NO_MATCH`는
 * 같은 밴드(`'attention'`)인데 **서류를 요구하는 사유가 달라 문구가 다르다**. 즉 배너는 밴드보다
 * 잘게 가르는 자기 축을 가지고, 그 축에는 래칫이 없었다(2026-10-20 후속).
 *
 * 없으면 생기는 일: 새 멤버를 `MANUAL_REVIEW_VERIFICATION_STATUSES`에 더하면 설정 화면은
 * 「심사 대기 중 — 지정서를 업로드해주세요」를 **자동으로** 내는데 배너는 `'unknown'`으로 떨어져
 * 「인증 진행 상태를 가져오는 중입니다」라 **약하게** 말한다 — 사람을 기다리는 일을 「가져오는 중」
 * 이라 하는 것이고, **두 화면이 또 갈린다.** 그 기제(같은 축을 두 곳이 각자 판정)가 이 레포에서
 * 세 번째다(`REVIEW_AMBIGUOUS` 누락 → 폴백 방향 → 이 자리).
 *
 * ## 읽는 법 둘
 * - **선언은 `Record<VerificationStatus, …>`로 두고 조회만 캐스트한다** — 선언을
 *   `Record<string, …>`으로 느슨하게 하면 tsc 강제가 죽어 이 파일이 하는 일이 없어진다.
 * - **`REVIEW_AMBIGUOUS`와 `REVIEW_NO_MATCH`는 「행동은 같고 서술은 다르다」** — 둘 다 지정서를
 *   올려야 하지만 **이유가 반대**다. 서버(`organization-verification.service.ts:172~198`)를 보면
 *   `NO_MATCH`는 **Top1 < threshold**(공시에서 못 찾았다)이고 `AMBIGUOUS`는
 *   **Top1 ≥ threshold ∧ Top2 ≥ threshold ∧ 격차 < margin**(찾았는데 경쟁 후보가 여럿이라 특정
 *   불가)이다. 그래서 **서류 미제출 쪽 문구는 갈라야 하고**(`'pending-review-ambiguous'`)
 *   **제출 완료 쪽은 공유한다**(「운영자 심사를 기다린다」는 둘 다 정확하다).
 *   ⚠️ 종전 배너는 두 상태를 `||`로 묶어 **「공시 데이터에 해당 기관이 등록되어 있지 않습니다」를
 *   `AMBIGUOUS`에도 보여 주고 있었다** — 사실과 반대다. 2026-10-20에 이 Record가 항목을 갈라 둔
 *   덕에 드러났고(그때 이 주석은 「사용자가 할 일이 동일하다」고만 적어 **서술 축을 덮고** 있었다),
 *   그것이 「항목이 둘로 보이면 한쪽만 바꿀 수 있다」의 첫 수확이다.
 */
const VERIFICATION_DISPLAY_MODE: Record<VerificationStatus, VerificationDisplayMode | DocsSplit> = {
  // 렌더하지 않는 둘 — `verificationBand`의 `'none'`과 같은 집합이어야 한다(spec이 교차 단언한다)
  NOT_REQUIRED: 'none',
  VERIFIED: 'none',
  PENDING: 'pending-retrying',
  INFO_MISMATCH: 'pending-info-mismatch',
  // 공시 조회가 시간 내 답을 못 준 것 — 서류로 대체 심사
  REVIEW_TIMEOUT: (d) => (d ? 'pending-review-timeout-submitted' : 'pending-review-timeout'),
  // 공시 Top1이 임계 미만 — 등록 자체가 확인되지 않는다
  REVIEW_NO_MATCH: (d) => (d ? 'pending-review-no-match-submitted' : 'pending-review-no-match'),
  // Top1·Top2가 둘 다 임계를 넘고 격차가 margin 미만 — **찾았는데 후보가 여럿**이다.
  // ⚠️ 미제출 쪽을 `'pending-review-no-match'`로 되돌리지 말 것 — 그 문구가 「등록되어 있지
  //    않습니다」라 **이 상태에는 거짓**이다(위 JSDoc). 제출 완료 쪽은 공유가 정확하다.
  REVIEW_AMBIGUOUS: (d) =>
    d ? 'pending-review-no-match-submitted' : 'pending-review-ambiguous',
  REVIEW_REAPPLIED: 'pending-review-reapplied',
  REJECTED: 'rejected',
};

/**
 * 상태(+서류 유무) → 배너 문구 모드.
 *
 * `'unknown'`은 **union 밖 문자열 전용**이다 — 서버가 먼저 새 상태를 쓰고 web 타입이 낡는 것이
 * 실제 순서이고, 그때 web은 그것이 심사 대역인지 **알 방법이 없으므로**(밴드도 `'unknown'`이다)
 * 「확인 중」이 정직한 최선이다. ⚠️ **union 멤버가 여기 떨어지면 위 `Record`에 구멍이 있는 것**이고
 * spec의 `[대조군]`이 그것을 red로 만든다.
 *
 * ⚠️ 그래서 「밴드가 `'attention'`인데 모드가 `'unknown'`이면 심사 문구로 돌린다」류 폴백 분기를
 * 두지 말 것 — 그 조건은 `MANUAL_REVIEW_VERIFICATION_STATUSES`가
 * `satisfies readonly VerificationStatus[]`라 **union 멤버에서만 참**이고, 이 `Record`가 그 전부를
 * 강제하므로 **도달 불가**다. 발동하지 않는 분기는 비용만이 아니라 거짓 안심이다.
 */
export function verificationDisplayMode(
  status: VerificationStatus | string | undefined | null,
  hasRequiredDocs: boolean,
): VerificationDisplayMode {
  if (!status) return 'none';
  // ⚠️ `Object.hasOwn`이 **필수다 — 인덱싱만 하면 프로토타입 체인이 보인다.** 그 입력은
  //    `string`이므로(바로 위 JSDoc: 서버가 먼저 새 상태를 쓰고 web 타입이 낡는 것이 실제
  //    순서) `'toString'`·`'valueOf'`·`'constructor'`가 올 수 있고, 그때 `entry`가
  //    `undefined`가 **아니라서** 폴백을 지나친다. 실측(node 22)한 결과가 셋 다 다르다:
  //      `'constructor'` → `typeof entry === 'function'`이라 `Object(hasRequiredDocs)`를 불러
  //                        **모드 자리에 객체**가 들어온다
  //      `'toString'`    → 같은 경로로 **`"[object Undefined]"`**라는 그럴듯한 **가짜 모드**
  //      `'valueOf'`     → `TypeError: Cannot convert undefined or null to object`로 **throw**,
  //                        이 함수가 `VerificationPendingBanner`의 렌더 경로라 **배너 트리가 죽는다**
  //    ⚠️ 그래서 「`undefined` 검사가 있으니 닫혔다」로 읽지 말 것 — 닫는 것은 **키 소유 검사**다.
  //    ⚠️ 같은 형태가 이 파일의 `getInvitationStatusBadgeTone`·`getSiteLifecycleBadgeTone`에도
  //       있으나 **층이 다르다**: 그 둘은 입력이 **닫힌 union**이라 상속 키가 타입상 올 수 없고,
  //       `|| 'slate'`가 받는 것은 느슨한 객체에서 온 옛 값뿐이다. 함께 고칠 필요 없다.
  // ⚠️ `Object.hasOwn`으로 바꾸지 말 것 — 그것은 **ES2022**이고 web은 `lib: ["ES2020", …]`이라
  //    `TS2550`이 난다(실측). 그 한 줄을 위해 프로젝트 `lib`을 올리는 것은 레포 전체의 허용
  //    문법을 바꾸는 일이라 이 축이 아니다. `in` 연산자도 **안 된다** — 프로토타입 체인을 본다.
  if (!Object.prototype.hasOwnProperty.call(VERIFICATION_DISPLAY_MODE, status)) return 'unknown';
  const entry = (VERIFICATION_DISPLAY_MODE as Record<string, VerificationDisplayMode | DocsSplit>)[
    status
  ];
  return typeof entry === 'function' ? entry(hasRequiredDocs) : entry;
}

/**
 * 카테고리가 **반드시 내야 하는** 검증 서류 — 「필수 서류를 냈는가」의 축 하나다.
 *
 * ## 왜 공용인가 — 두 화면이 그 질문에 «다른 술어»로 답하고 있었다 (2026-10-21)
 * - `components/VerificationPendingBanner.tsx` — `isWorkplace ? 사업자등록증 : 지정서`가 **있나**
 * - `pages/settings/Organization.tsx` — `verificationDocuments.length > 0`(**아무** 서류)
 *
 * 갈리는 입력이 실재한다: 기관 조직이 `REVIEW_TIMEOUT`에서 **사업자등록증만** 올리면
 * (`handleBatchUpload`가 두 칸을 **독립적으로** 보내므로 가능하다) 설정 화면 제목이
 * 「기관 인증 심사 진행 중 — **지정서 제출 완료**」라 **거짓**을 말하고 배너는 옳게 말했다.
 *
 * ⚠️ **분류 규칙도 사본이었다** — `(d.type ?? 'DESIGNATION_CERT') === 'DESIGNATION_CERT'`가
 * 정확히 그 두 파일에만 있었고 배너 쪽 주석이 「Organization.tsx와 **동일한 기준**」이라 **자진
 * 신고**하고 있었다. 그래서 분류까지 {@link classifyVerificationDocuments}로 함께 올린다.
 *
 * `?? 'DESIGNATION_CERT'`가 기본값인 것은 **지정서가 먼저 생겼고** 그 시절 행에 `type`이 없기
 * 때문이다(서버 jsonb 거울의 옛 형상) — 바꾸면 그 행들이 분류를 잃는다.
 */
export function requiredVerificationDocType(
  category: OrganizationCategory | string | undefined | null,
): VerificationDocumentType {
  return category === 'WORKPLACE' ? 'BUSINESS_REGISTRATION' : 'DESIGNATION_CERT';
}

/**
 * 서류를 종류별로 가른다 — 두 화면이 같은 기준을 쓰게 하는 자리.
 *
 * 제네릭인 이유: 호출부의 형상이 **둘**이다. 설정 화면은 {@link VerificationDocument}(응답 타입)를
 * 들고 렌더까지 하고, 배너는 `auth.ts`의 `agencyVerificationDocuments`(`unknown[]`을
 * `Array.isArray`로 좁힌 느슨한 형상)를 들고 **개수만** 센다. 분류 기준은 같으므로 타입만 열어 둔다.
 */
export function classifyVerificationDocuments<T extends { type?: string | null }>(
  docs: readonly T[],
): { businessRegistration: T[]; designation: T[] } {
  return {
    businessRegistration: docs.filter((d) => d.type === 'BUSINESS_REGISTRATION'),
    designation: docs.filter((d) => (d.type ?? 'DESIGNATION_CERT') === 'DESIGNATION_CERT'),
  };
}

/**
 * **필수 서류를 냈는가** — {@link verificationDisplayMode}의 둘째 인자를 만드는 유일한 자리.
 *
 * ⚠️ 이 함수가 `lib/organizations`에 사는 이유는 **그 함수가 이 값을 입력으로 요구하기 때문**이다.
 * 판정을 호출부에 두면 「모드는 한 자리에서 나온다」는 규약이 **입력 쪽으로 새고**, 실제로 그렇게
 * 새어 두 화면이 갈렸다(위 {@link requiredVerificationDocType} JSDoc).
 */
export function hasRequiredVerificationDocs(
  docs: readonly { type?: string | null }[],
  category: OrganizationCategory | string | undefined | null,
): boolean {
  const { businessRegistration, designation } = classifyVerificationDocuments(docs);
  return requiredVerificationDocType(category) === 'BUSINESS_REGISTRATION'
    ? businessRegistration.length > 0
    : designation.length > 0;
}

/**
 * **서류 제출 창구** — 상태가 정하는 「무엇을 받나」. `'none'`은 렌더하지 않는다는 뜻이다.
 *
 * ⚠️ 이것은 {@link VerificationBand}·{@link VerificationDisplayMode}와 **또 다른 축**이다. 밴드는
 * 색·아이콘, 모드는 문구, 이것은 **컨트롤**을 정한다. 셋을 한 술어로 합치지 말 것 — 입도가 다르다
 * (`REVIEW_TIMEOUT`과 `REVIEW_NO_MATCH`는 밴드가 같고 문구가 다르고 **창구도 다르다**).
 */
export type VerificationUploadSurface =
  | 'none'
  | 'auto-retrying'
  | 'required-only'
  | 'required-and-business'
  | 'reapply';

/**
 * 상태 → 창구. **`Record`라 shared에 멤버가 늘면 tsc가 미기재를 거부한다.**
 *
 * ## 왜 생겼나 — 수동 심사 대역 넷 중 **둘이 막다른 길**이었다 (2026-10-21)
 * `settings/Organization.tsx`의 본문은 창구를 `=== 'PENDING'` / `=== 'REVIEW_TIMEOUT'` /
 * `=== 'REVIEW_NO_MATCH'` / `=== 'REJECTED' || === 'INFO_MISMATCH'` **네 자리로 손열거**하고
 * 나머지를 `return null`로 떨어뜨렸다. 그래서 {@link MANUAL_REVIEW_VERIFICATION_STATUSES} 넷 중
 * `REVIEW_AMBIGUOUS`·`REVIEW_REAPPLIED`는 **업로드 컨트롤이 0개**였는데, 같은 화면의 제목이
 * 「기관 인증 심사 대기 중 — **지정서를 업로드해주세요**」라 말하고 형제 배너가 「서류
 * 업로드하기」 버튼으로 **그리로 보냈다**(`VerificationPendingBanner`의 `pending-review-ambiguous`).
 *
 * **밴드 `'unknown'`도 같은 자리로 떨어졌다** — 그쪽은 더 나빴다: 그 화면이 「모르는 상태는
 * **의도적으로 포함**한다 … 막으면 서버가 새 상태를 쓴 날 사용자가 **아무것도 못 하게** 된다」고
 * 적고 본문이 「서류 제출은 아래에서 **그대로 진행할 수 있습니다**」라 말하는데, 네 분기 중 어느
 * 것도 안 걸려 **그 두 문장이 거짓**이었다. 그래서 union 밖은 **가장 넓은 창구**를 준다.
 *
 * ## 서버가 받는 집합 — 이 Record의 근거
 * `organizations.service.ts`의 `uploadVerificationDocument`는 **`PENDING` 하나만 거부**한다
 * (`ORGANIZATION_VERIFICATION_NOT_ESCALATED`). 즉 **창구를 닫아야 하는 상태는 그것뿐**이고,
 * 나머지에서 컨트롤을 안 그리는 것은 화면이 **서버보다 좁게** 구는 것이다.
 *
 * ## `'required-only'`와 `'required-and-business'`의 가름 = 「NTS가 해소됐나」
 * `REVIEW_TIMEOUT`은 국세청 진위확인 자체가 시간 내 답을 못 준 것이라 **사업자등록증도** 받아야
 * 한다. `REVIEW_NO_MATCH`·`REVIEW_AMBIGUOUS`는 NTS를 **통과하고** 공시 매칭에서 막힌 것이므로
 * 카테고리 필수 서류(기관=지정서)만 받는다 — 서버 판정은
 * `organization-verification.service.ts:140~198`.
 *
 * ⚠️ **`REVIEW_NO_MATCH`·`REVIEW_AMBIGUOUS`는 WORKPLACE에 도달하지 않는다** — 그 서비스
 * `:147~153`이 WORKPLACE를 NTS 통과 즉시 `VERIFIED`로 **early-return**하고 공시 매칭에 들어가지
 * 않는다. 종전 코드의 `&& !_isWorkplace` 가드는 그래서 **결함이 아니었다**(도달 불가 자리).
 * 그 가드를 지운 것은 동작을 넓히는 것이 아니라 **창구를 카테고리에서 파생**시켜 같은 답을
 * 구조로 얻는 것이다: WORKPLACE의 필수 서류가 사업자등록증이므로 두 창구 값이 **한 칸으로 접힌다**.
 *
 * ⚠️ `REVIEW_REAPPLIED`는 **WORKPLACE에도 도달한다**(운영자가 반려한 뒤 서류를 올리면 전환된다 —
 * `organizations.service.ts:5415`) ⇒ 그 조합이 `null`이던 것은 **실재하는 막다른 길**이었다.
 */
const VERIFICATION_UPLOAD_SURFACE: Record<VerificationStatus, VerificationUploadSurface> = {
  // 상위 게이트(밴드 `'none'`)가 이미 배제하므로 화면에서는 도달 불가 — 값은 교차 단언용이다
  NOT_REQUIRED: 'none',
  VERIFIED: 'none',
  // 서버가 업로드를 **거부**한다 — 컨트롤을 그리면 눌렀을 때 실패하는 화면이 된다
  PENDING: 'auto-retrying',
  // NTS 자체가 미해소 — 사업자등록증도 받는다
  REVIEW_TIMEOUT: 'required-and-business',
  // NTS는 통과했고 공시에서 막힌 것 — 카테고리 필수 서류만
  REVIEW_NO_MATCH: 'required-only',
  REVIEW_AMBIGUOUS: 'required-only',
  // 반려 뒤 재업로드로 들어온 상태 — 다시 올릴 수 있어야 한다(이 값이 없어 막다른 길이었다)
  REVIEW_REAPPLIED: 'required-only',
  // 사용자가 **사업자 정보를 고쳐** 재검증을 받는 경로 — 컨트롤 모양이 위 배치 업로드와 다르다
  REJECTED: 'reapply',
  INFO_MISMATCH: 'reapply',
};

/**
 * 상태 → 서류 제출 창구.
 *
 * union 밖 문자열은 **가장 넓은 창구**(`'required-and-business'`)다 — 사유는 위 Record JSDoc의
 * 「밴드 `'unknown'`도 같은 자리로 떨어졌다」 절. ⚠️ 그것을 `'none'`이나 `'auto-retrying'`으로
 * 되돌리지 말 것: 앞은 **배너가 가리키는 화면에 아무것도 없게** 만들고, 뒤는 **서버가 받는데 화면이
 * 거부하는** 상태를 만든다. ⚠️ **union 멤버가 여기 떨어지면 위 `Record`에 구멍이 있는 것**이고
 * spec의 `[대조군]`이 그것을 red로 만든다.
 */
export function verificationUploadSurface(
  status: VerificationStatus | string | undefined | null,
): VerificationUploadSurface {
  if (!status) return 'none';
  // ⚠️ 키 소유 검사가 필수다(상속 키 — 사유·실측은 위 `verificationDisplayMode`의 그 주석).
  //    `Object.hasOwn`은 ES2022라 web의 `lib: ES2020`에서 `TS2550`이고, `in`은 체인을 본다.
  if (!Object.prototype.hasOwnProperty.call(VERIFICATION_UPLOAD_SURFACE, status)) {
    return 'required-and-business';
  }
  return (VERIFICATION_UPLOAD_SURFACE as Record<string, VerificationUploadSurface>)[status];
}

/**
 * 반려 사유 라벨 — **정본은 shared의 `VERIFICATION_REJECTION_REASON_LABELS`**이고 여기는 별칭이다.
 *
 * ⚠️ 런타임 값은 종전에도 shared의 **전체 맵**(legacy 3종 포함)이었다 — 바뀐 것은 타입뿐이고
 * 화면 동작은 그대로다. DB에 남은 legacy 코드(`BUSINESS_CLOSED` 등)가 라벨을 찾던 것도 그대로다.
 */
export const REJECTION_REASON_LABELS: Record<VerificationRejectionReason, string> =
  VERIFICATION_REJECTION_REASON_LABELS;

// 탈퇴 시나리오 타입
export type LeaveScenario = 'OTHER_OWNERS' | 'TRANSFER_REQUIRED' | 'LAST_MEMBER';

export interface VerificationDocument {
  id: string;
  /**
   * ⚠️ **`href`로 쓰지 말 것 — 2026-10-19부터 인증 전용 라우트다.** 서버 인증이 Bearer 헤더
   * 전용이라 평범한 내비게이션은 401이다. 열람은 {@link organizationsApi.downloadVerificationDocument}
   * (blob)로 하고, 이 키는 **형상 호환**으로 남아 있다(캐시된 옛 번들의 `href`가 그 값을 쓴다).
   */
  url: string;
  fileName: string;
  uploadedAt: string;
  type?: VerificationDocumentType;
}

export interface Organization {
  id: string;
  name: string | null;
  displayName: string;
  type: OrganizationType;
  category: OrganizationCategory;
  timezone: string;
  allowNonAssignedSiteView: boolean; // 비담당 안전관리자 현장/점검/보고서/TBM 조회 허용
  aiMeasureTone: AiMeasureTone | null; // AI 개선대책 문체 프리셋. null = 조직 유형에서 파생(resolveAiMeasureTone)
  equipmentList: string[]; // 측정 장비 목록 (안전관리상태보고서용)
  equipmentUnits: Record<string, string>; // 장비명 → 단위 매핑
  logoPath?: string | null; // 조직 로고 스토리지 경로
  companyId?: string | null;
  company?: Company | null;
  groups?: Group[];
  // 검증 관련 필드 (특수 조직 전용)
  verificationStatus?: VerificationStatus;
  verifiedAt?: string;
  rejectionReason?: string;
  rejectionReasonCode?: VerificationRejectionReason;
  verificationDocuments?: VerificationDocument[];
  createdAt: string;
  updatedAt: string;
}

// 인증 제공자 타입
export type AuthProvider = 'EMAIL' | 'ID' | 'KAKAO' | 'GOOGLE';

export interface UserAuth {
  provider: AuthProvider;
  providerId: string;
}

export interface OrganizationMemberSiteContact {
  siteId: string;
  siteName: string;
  department: string | null;
  position: string | null;
  isActive: boolean;
}

/** 멤버 목록 응답의 소속 그룹 항목 (`GET /organizations/:id/members`). */
export interface OrganizationMemberGroup {
  id: string;
  name: string;
}

export interface OrganizationMember {
  id: string;
  memberId?: string;
  userId?: string;
  companyId: string;
  /**
   * 소유자 성질(company-rbac P4 ① — 서버 `getMembers`가 `listOwnerMemberIds`로 싣는다). 「다른 소유자가 남나」「이전받을 사람이
   * 있나」 같은 판정과 역할 배지(`memberKindOf`)가 이 필드를 읽는다. 옛 `role`은 P7-b-2에서 지워졌다.
   */
  isOwner?: boolean;
  /**
   * 조직 **구성원 자격**(company-rbac P7-b-1 — 서버 `getMembers`가 `listOrganizationMemberIds`로 싣는다): 활성 배정 중
   * `countsAsMember` 역할이 하나라도 있나. `isOrganizationMember`(`lib/members.ts`)의 유일한 원천 — 옛 `role != null`을 대체한다.
   * `site-staff`(현장 담당자)만 가진 사람은 false(옛 role NULL과 같은 답), 소유자가 현장 담당자로도 등록된 겸직은 true.
   * `getMembers` 응답에는 항상 있다(옵셔널인 이유는 다른 경로가 만든 옛 형상 객체 때문 — 없으면 구성원이 아니다).
   */
  countsAsMember?: boolean;
  /**
   * 표시용 역할 목록(P7-b-2 — 서버 `getMembers`가 활성 배정 + 역할 이름으로 싣는다, sortOrder 순).
   * **배열인 이유**: 겸직이 정상이고(소유자가 현장 담당자로도 등록되는 등) 시드만 12종이라 「소유자/구성원」
   * 2종으로는 못 담는다. 빈 배열이면 배정이 없는 것 = 외부 인원(`countsAsMember: false`와 같은 답).
   * ⚠️ **판정에 쓰지 말 것** — 소유자 여부는 `isOwner`, 구성원 자격은 `countsAsMember`다(서버가 조직
   * 오버라이드까지 보고 판정한다). 이 배열에서 다시 파생하면 오버라이드를 못 봐 서버와 갈린다.
   */
  roles?: { key: string; name: string }[];
  rank?: string | null; // 직급 (사원/대리/과장/부장 등). CompanyMember 단위 1값
  user?: {
    id: string;
    name: string;
    phone?: string | null;
    rank?: string | null; // 직급. Phase F-1
    deletedAt?: string | null; // 탈퇴일
    /** 마지막 로그인 (ISO). 로그인 적재 이전 가입자는 null */
    lastLoginAt?: string | null;
    auths: UserAuth[];
    profileImageUrl?: string;
  } | null;
  member?: {
    id: string;
    name: string;
    phone?: string | null;
    email?: string | null;
    deletedAt?: Date | string | null;
  } | null;
  siteAssignments: SiteAssignment[];
  /** 멤버가 속한 현장별 SiteContact (소속/직책 참조용) */
  siteContacts?: OrganizationMemberSiteContact[];
  /**
   * 멤버가 속한 그룹. Owner는 GroupMember 행이 없어(묵시적 전 그룹) 빈 배열이고,
   * 그룹을 쓰지 않는 조직은 전원 빈 배열이다.
   */
  groups?: OrganizationMemberGroup[];
  createdAt: string;
}

// 탈퇴 계정 술어(`isWithdrawnMember`)는 `./memberStatus`에 산다 — 이 모듈은 spec 15곳이
// `vi.mock`으로 통째 대체하므로 순수 술어를 여기 두면 그 spec들이 전부 깨진다. 소비처는
// `lib/memberStatus`에서 직접 import할 것(여기서 re-export하지 않는다 — mock 팩토리가 모른다).

/**
 * auths 배열에서 사용자 식별자를 추출합니다.
 * 우선순위: ID > EMAIL > KAKAO > GOOGLE
 */
export function getUserIdentifier(auths: UserAuth[]): string {
  const idAuth = auths.find((a) => a.provider === 'ID');
  if (idAuth) return idAuth.providerId;

  const emailAuth = auths.find((a) => a.provider === 'EMAIL');
  if (emailAuth) return emailAuth.providerId;

  const kakaoAuth = auths.find((a) => a.provider === 'KAKAO');
  if (kakaoAuth) return '카카오 로그인';

  const googleAuth = auths.find((a) => a.provider === 'GOOGLE');
  if (googleAuth) return '구글 로그인';

  return '-';
}

// 점검 상태 (서버와 동일)
export type InspectionStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'PENDING_REVIEW' | 'COMPLETED';
// 보고서 상태는 `lib/reports.ts`가 정본 — 현장 목록이 최신 보고서를 더 이상 읽지 않으면서
// 여기 있던 중복 정의는 소비처가 사라졌다(결정 기록 `2026-08-03-site-status-axes`).

export interface Site {
  id: string;
  companyId: string;
  /** 소속 그룹 (NOT NULL — 본점 backfill 완료). 그룹 필터 스코프의 축. */
  groupId: string;
  /**
   * 호출 조직 기준 그 현장이 놓인 그룹(shared `SiteOperatorGroupField` — 소유 조직이면 `groupId` · 대행 기관이면 배치 그룹).
   * 서버가 **상세 GET·수정 PATCH 응답에만** 싣는다(목록 응답엔 없다 — 그래서 optional). 대행 기관 화면의 「소속 그룹」 표시는
   * `groupId`(남의 조직 그룹)가 아니라 이 값을 읽는다.
   */
  operatorGroupId?: SiteOperatorGroupField['operatorGroupId'];
  /**
   * 호출 조직이 현장 정보를 고칠 수 있는가(shared `SiteInfoManageField` — O10·Q8). 서버가 **목록 항목·상세 GET·수정 PATCH 응답**에
   * 싣는다(현장 생성 응답·실시간 갱신 행에는 없을 수 있어 optional). 없으면 막지 않는다 — 판정은 서버 403이 정본이고 이 칸은 미리
   * 막는 재료다. 읽는 자리는 `lib/siteInfoManage`의 `isSiteInfoLocked` 하나로 모은다.
   */
  canManageSiteInfo?: SiteInfoManageField['canManageSiteInfo'];
  name: string;
  address?: string;
  addressDetail?: string;
  zonecode?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  /** 레거시 자유 텍스트 업종 — 읽기 전용(폼은 쓰지 않는다). 표시는 `lib/siteIndustry`가 KSIC 다음 폴백으로 읽는다 */
  industryType?: string;
  industrySubType?: string;
  /** 업종 분류(KSIC) `Industry.id` — 정본(법정 서류 적용 판정이 읽는다) */
  industryId?: string | null;
  /** 업종 분류(KSIC) — 서버 `SITE_INDUSTRY_INCLUDE`(목록·상세·수정 응답이 같은 형상) */
  industry?: { id: string; code: string; name: string; level: number } | null;
  hazardCategories?: string[];
  inspectionChecklist?: unknown;
  adhocCategories?: unknown;
  riskAssessmentConfigId?: string | null;
  riskAssessmentMeta?: { categories: string[]; processes: string[]; departments: string[]; contacts?: Array<{ id: string; name: string; department?: string; position?: string; phone?: string }> } | null;
  constructionCost?: string;    // Decimal → string
  constructionStart?: string;   // ISO 8601
  constructionEnd?: string;
  managementNumber?: string;
  licenseNumber?: string;
  progressRate?: number;
  clientName?: string;
  clientContact?: string;
  // 생산품 (예: "반도체 웨이퍼, PCB") — 안전관리전문기관 전용 수집
  products?: string | null;
  // Sprint 1에서 추가된 필드
  company?: Company;
  // Sprint 3에서 추가된 필드
  constructionCategory?: string;  // 'CONSTRUCTION' | 'ELECTRICAL_TELECOM' | null
  /**
   * 현장 생애주기 — 저장 상태 단일 축(`isActive` boolean은 2026-08-03에 이 enum으로 흡수).
   * 점검·보고서 진행은 별개 축이라 여기 섞지 않는다(결정 기록 `2026-08-03-site-status-axes`).
   */
  status: SiteStatus;
  createdAt: string;
  updatedAt: string;
  // 담당자 목록 (목록 조회 시 포함)
  assignments?: SiteAssignmentWithMember[];
  // 점검 수 (목록 조회 시 포함, 삭제된 점검 제외)
  _count?: {
    inspections: number;
  };
  // 마지막 점검 정보 (목록 조회 시 포함)
  inspections?: {
    id: string;
    status: InspectionStatus;
    createdAt: string;
  }[];
  // 발견사항 집계 (목록 조회 시 포함)
  findingStats?: {
    total: number;
    resolved: number;
    unresolved: number;
    /** 미해소 중 위험도 최상위 등급(점검의 유효 위험성평가 config 기준) 건수. */
    unresolvedHigh?: number;
    /** 최근 7일(오늘 포함, Asia/Seoul 달력) 신규 발견 건수. */
    found7d?: number;
    /** 최근 7일(같은 창) 해소 건수. */
    resolved7d?: number;
  };
}

// 담당자 정보가 포함된 SiteAssignment
export interface SiteAssignmentWithMember {
  id: string;
  memberId: string;
  siteId: string;
  assignmentType?: AssignmentType;
  member?: {
    id: string;
    /**
     * 조직을 떠난 멤버십이면 시각이 찍힌다(CompanyMember.left_at). 목록 조회가 CompanyMember
     * 행을 통째로 실어 원래 내려오던 값인데 타입에만 없었다 — 담당자 공백 판정이 서버와 같은
     * 축(`site-primary-vacancy.util`)을 쓰려면 필요해 선언만 맞춘다(응답 계약 변경 아님).
     */
    leftAt?: string | null;
    // Phase D: OrganizationMember.member (Member 통일 모델)
    member?: {
      id: string;
      userId: string;
      name: string;
      phone?: string | null;
      user?: { auths?: { providerId: string }[] };
    };
  };
  createdAt: string;
}

/**
 * 담당자 배정 유형 — **정본은 `@safetyhub/shared`의 `SiteAssignmentType`**이고 여기는 별칭이다.
 *
 * 종전엔 같은 union이 세 벌이었다(서버 prisma enum / 여기 / admin은 `string`). shared 한 벌로 모으면
 * 서버 어댑터(`apps/server/src/sites/primary-designation-wall.ts`)의 양방향 `Record` 미러가 prisma enum과의
 * 동치를 tsc에 강제하므로, 값이 늘 때 **화면만 모르는 상태**가 생기지 않는다. 별칭으로 두는 이유는 소비처
 * 20여 곳이 `./organizations`에서 이 이름을 가져가기 때문이다(경로를 바꾸는 것은 이 변경의 축이 아니다).
 */
export type AssignmentType = SiteAssignmentType;

export interface SiteAssignment {
  id: string;
  memberId: string;
  siteId: string;
  assignmentType?: AssignmentType;
  site?: Site;
  member?: OrganizationMember;
  createdAt: string;
}

// DTOs
export interface CreateOrganizationDto {
  name?: string; // 검증 대상(WORKPLACE/기관)은 상호가 조직명 → optional. TEAM/내부만 필수
  category?: OrganizationCategory;
  timezone?: string;
  company?: CreateCompanyDto;
  // 특수 조직 전용 (GUIDANCE_AGENCY / SAFETY_MANAGEMENT_AGENCY) + WORKPLACE NTS 검증
  agencyBusinessNumber?: string;
  agencyOpeningDate?: string;
  agencyCeoName?: string;
  agencyOfficialName?: string;
  agencyAddress?: string;
  agencyZonecode?: string;
  agencyAddressDetail?: string;
  // 사업자번호 중복 시 사용자가 "지점으로 추가" 확인 시 true
  confirmDuplicateBusinessNumber?: boolean;
}

export interface UpdateOrganizationDto {
  name?: string;
  category?: OrganizationCategory;
  timezone?: string;
  allowNonAssignedSiteView?: boolean;
  aiMeasureTone?: AiMeasureTone | null; // null = 조직 유형 기본값으로 되돌림
  equipmentList?: string[];
  equipmentUnits?: Record<string, string>;
  company?: CreateCompanyDto;
  // 특수 조직 전용 (GUIDANCE_AGENCY / SAFETY_MANAGEMENT_AGENCY) — 재검증 시 사용
  agencyBusinessNumber?: string;
  agencyOpeningDate?: string;
  agencyCeoName?: string;
  agencyOfficialName?: string;
  agencyAddress?: string;
  agencyZonecode?: string;
  agencyAddressDetail?: string;
}

export interface InviteMemberDto {
  identifier?: string;  // 이메일 또는 아이디 (@ 포함 시 이메일로 처리)
  phone?: string;
  /**
   * 조직 RBAC 역할 key(#56 역할 멀티셀렉트, company-rbac P2-a) — 합류 시 배정 행이 생긴다. P7-b-2부터 초대의 유일한 역할 축:
   * 소유자 초대는 `[OWNER_ROLE_KEY]`, 그 외는 선택한 key(비면 서버가 `member`를 준다).
   */
  roleKeys?: string[];
  siteId?: string;
}

// Invitation Types
export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'CANCELLED';

export interface OrganizationInvitation {
  id: string;
  companyId: string;
  siteId?: string;
  email?: string;
  phone?: string;
  username?: string;
  /** 초대와 함께 배정될 역할 key(P7-b-2 — 옛 `role` 대체). 소유자 초대는 `OWNER_ROLE_KEY`를 포함한다 */
  roleKeys?: string[];
  token: string;
  status: InvitationStatus;
  invitedBy: string;
  expiresAt: string;
  acceptedAt?: string;
  acceptedBy?: string;
  createdAt: string;
  updatedAt?: string;
  /** H-8e: organization 제거 → subscription.company 평면화. companyId + company로 접근 */
  company?: {
    id: string;
    name: string;
  };
  /** @deprecated H-8e에서 서버가 더 이상 반환하지 않음. company 필드를 사용하세요. */
  organization?: {
    id: string;
    name: string;
  };
  /** H-8e: subscription.company를 통해 조직 정보 전달 */
  subscription?: {
    company?: {
      id: string;
      name: string;
    };
  };
  site?: {
    id: string;
    name: string;
  };
  inviter: {
    id: string;
    name: string;
  };
  isUserRegistered?: boolean;
}

export interface InviteMemberResult {
  type: 'MEMBER_ADDED' | 'INVITATION_CREATED';
  member?: OrganizationMember;
  invitation?: OrganizationInvitation;
  message: string;
}

/** 소유자 지정/해제(P7-b-2 — 옛 `role: OWNER | SAFETY_MANAGER` 토글이 남긴 유일한 의미). 그 외 역할은 `rbacApi` 배정 */
export interface UpdateMemberRoleDto {
  isOwner: boolean;
}

// 조직 탈퇴 관련 타입
export interface LeaveOrganizationOptions {
  transferToUserId?: string;
  forceDelete?: boolean;
}

export interface LeaveOrganizationResult {
  success?: boolean;
  message?: string;
  requiresChoice?: boolean;
  safetyManagers?: {
    userId: string;
    memberId: string;
    userName: string;
  }[];
}

export interface CreateSiteDto {
  name: string;
  address?: string;
  addressDetail?: string;
  zonecode?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  industryType?: string;
  industrySubType?: string;
  /** 업종 분류(KSIC) `Industry.id` — 어느 단계든 저장된다 */
  industryId?: string;
  hazardCategories?: string[];
  /**
   * 공사금액 — **숫자**로 보낸다. 서버 `CreateSiteDto.constructionCost`가 `@IsNumber`이고 전역
   * ValidationPipe에 암묵 변환이 없어 문자열은 400이다(리뷰 1103 F3). 응답(`Site.constructionCost`)은
   * Decimal 직렬화라 문자열이다 — 보내는 형과 받는 형이 다르다.
   */
  constructionCost?: number;
  constructionStart?: string;
  constructionEnd?: string;
  managementNumber?: string;
  licenseNumber?: string;
  progressRate?: number;
  clientName?: string;
  clientContact?: string;
  products?: string;
  /** 소속 그룹 (미지정 시 본점). 지정은 Owner 전용 — 비Owner가 보내면 서버가 403 */
  groupId?: string;
  // Sprint 1에서 추가된 필드
  companyId?: string;
  // Sprint 3에서 추가된 필드
  constructionCategory?: string;
  // NTS 검증 경로 (companyId 없이 업체 생성/승격을 맡기는 경우)
  companyInfo?: import('./companies').CompanyInfoInput;
}

export interface UpdateSiteDto {
  name?: string;
  address?: string;
  addressDetail?: string;
  zonecode?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  industryType?: string;
  industrySubType?: string;
  /** 업종 분류(KSIC) `Industry.id` — `null`이면 지움 · 안 보내면 그대로 */
  industryId?: string | null;
  hazardCategories?: string[];
  inspectionChecklist?: unknown;
  adhocCategories?: unknown;
  riskAssessmentConfigId?: string;
  riskAssessmentMeta?: { categories: string[]; processes: string[]; departments: string[]; } | null;
  status?: SiteEditableStatus;
  /** 숫자로 보낸다 — `CreateSiteDto.constructionCost` JSDoc */
  constructionCost?: number;
  constructionStart?: string;
  constructionEnd?: string;
  managementNumber?: string;
  licenseNumber?: string;
  progressRate?: number;
  clientName?: string;
  clientContact?: string;
  products?: string | null;
  /** 소속 그룹 이동 (Owner 전용). 변경할 때만 보낸다 — 비Owner가 보내면 서버가 403 */
  groupId?: string;
  // Sprint 1에서 추가된 필드
  companyId?: string | null;
  // Sprint 3에서 추가된 필드
  constructionCategory?: string | null;
  // NTS 검증 경로 (companyId 없이 업체 생성/승격을 맡기는 경우)
  companyInfo?: import('./companies').CompanyInfoInput;
}

// 현장 통계 응답 타입
export interface SiteStatsResponse {
  inspections: {
    total: number;
    inProgress: number;
    pendingReview: number;
    completed: number;
  };
  reports: {
    total: number;
    draft: number;
    pendingApproval: number;
    approved: number;
    rejected: number;
  };
  findings: {
    total: number;
    bySeverity: {
      LOW: number;
      MEDIUM: number;
      HIGH: number;
      CRITICAL: number;
    };
    unresolved: number;
  };
  tbms: {
    total: number;
    created: number;
    inProgress: number;
    completed: number;
  };
}

// 기간 타입
export type TrendPeriodType = 'daily' | 'weekly' | 'monthly';

// 위험요인 추이 응답 타입
export interface FindingsTrendPeriod {
  label: string;
  periodStart: string;
  periodEnd: string;
  discovered: number;
  resolved: number;
  unresolved: number;
}

export interface FindingsTrendResponse {
  period: TrendPeriodType;
  periods: FindingsTrendPeriod[];
}

export interface AssignMemberDto {
  memberId: string;
  setPrimary?: boolean;
}

/** 멤버 평균 활동도 (멤버 관리 '평균 활동도' 카드) */
export interface MemberActivityStats {
  /** 재직 멤버 1인당 하루 평균 로그인 횟수. 집계 대상 멤버가 없으면 null */
  avgDailyLoginsPerMember: number | null;
  /** 직전 같은 길이 창 대비 변화율(정수 %) — 직전 창 로그인이 0이면 null(비교 불가) */
  changePercent: number | null;
  /** 집계 창(일) */
  windowDays: number;
}

// Organization API
export const organizationsApi = {
  // 조직 생성
  create: async (data: CreateOrganizationDto): Promise<Organization> => {
    const response = await api.post<ApiResponse<Organization>>('/organizations', data);
    return response.data.data!;
  },

  // 조직 조회
  findById: async (organizationId: string): Promise<Organization> => {
    const response = await api.get<ApiResponse<Organization>>(`/organizations/${organizationId}`);
    return response.data.data!;
  },

  // 조직 수정
  update: async (organizationId: string, data: UpdateOrganizationDto): Promise<Organization> => {
    const response = await api.patch<ApiResponse<Organization>>(
      `/organizations/${organizationId}`,
      data
    );
    return response.data.data!;
  },

  // 조직 삭제
  delete: async (organizationId: string): Promise<void> => {
    await api.delete(`/organizations/${organizationId}`);
  },

  // 조직 로고 업로드
  uploadLogo: async (organizationId: string, file: File): Promise<{ logoPath: string; logoUrl: string }> => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.patch<ApiResponse<{ logoPath: string; logoUrl: string }>>(
      `/organizations/${organizationId}/logo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data.data!;
  },

  // 조직 로고 URL 가져오기 (직접 img src에 사용)
  getLogoUrl: (organizationId: string): string => {
    const API_HOST = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    return `${API_HOST}/api/v1/organizations/${organizationId}/logo`;
  },

  // 조직 로고 삭제
  deleteLogo: async (organizationId: string): Promise<void> => {
    await api.delete(`/organizations/${organizationId}/logo`);
  },

  // 삭제된 조직 복구
  restore: async (organizationId: string): Promise<{ message: string }> => {
    const response = await api.post<ApiResponse<{ message: string }>>(
      `/organizations/${organizationId}/restore`
    );
    return response.data.data!;
  },

  // 멤버 목록 조회
  getMembers: async (organizationId: string): Promise<OrganizationMember[]> => {
    const response = await api.get<ApiResponse<OrganizationMember[]>>(
      `/organizations/${organizationId}/members`
    );
    return response.data.data!;
  },

  // 멤버 평균 활동도 조회 (최근 N일 1인당 하루 평균 로그인 횟수)
  getMemberActivityStats: async (organizationId: string): Promise<MemberActivityStats> => {
    const response = await api.get<ApiResponse<MemberActivityStats>>(
      `/organizations/${organizationId}/member-activity-stats`
    );
    return response.data.data!;
  },

  // 멤버 초대 (기존 사용자 즉시 추가 또는 대기 초대 생성)
  inviteMember: async (organizationId: string, data: InviteMemberDto): Promise<InviteMemberResult> => {
    const response = await api.post<ApiResponse<InviteMemberResult>>(
      `/organizations/${organizationId}/members`,
      data
    );
    return response.data.data!;
  },

  // 대기 중인 초대 목록 조회
  getInvitations: async (organizationId: string): Promise<OrganizationInvitation[]> => {
    const response = await api.get<ApiResponse<OrganizationInvitation[]>>(
      `/organizations/${organizationId}/invitations`
    );
    return response.data.data!;
  },

  // 초대 취소
  cancelInvitation: async (organizationId: string, invitationId: string): Promise<void> => {
    await api.delete(`/organizations/${organizationId}/invitations/${invitationId}`);
  },

  // 초대 재발송
  resendInvitation: async (organizationId: string, invitationId: string): Promise<OrganizationInvitation> => {
    const response = await api.post<ApiResponse<OrganizationInvitation>>(
      `/organizations/${organizationId}/invitations/${invitationId}/resend`
    );
    return response.data.data!;
  },

  // 멤버 소유자 지정/해제(P7-b-2 — 옛 역할 변경)
  updateMemberRole: async (
    organizationId: string,
    memberId: string,
    data: UpdateMemberRoleDto
  ): Promise<OrganizationMember> => {
    const response = await api.patch<ApiResponse<OrganizationMember>>(
      `/organizations/${organizationId}/members/${memberId}/role`,
      data
    );
    return response.data.data!;
  },

  // (멤버 세부 권한 변경 updateMemberPermissions — 옛 boolean canManage* — 는 P7-a에서 서버 컬럼과 함께 제거.
  //  멤버별 권한은 역할 배정(`rbacApi`)으로 편집한다)

  // 멤버 정보 수정
  updateMemberInfo: async (
    organizationId: string,
    memberId: string,
    data: { rank?: string }
  ): Promise<OrganizationMember> => {
    const response = await api.patch<ApiResponse<OrganizationMember>>(
      `/organizations/${organizationId}/members/${memberId}/info`,
      data
    );
    return response.data.data!;
  },

  // 멤버 제거
  removeMember: async (organizationId: string, memberId: string): Promise<void> => {
    await api.delete(`/organizations/${organizationId}/members/${memberId}`);
  },

  // 조직 탈퇴 (본인)
  leave: async (
    organizationId: string,
    options?: LeaveOrganizationOptions
  ): Promise<LeaveOrganizationResult> => {
    const response = await api.post<ApiResponse<LeaveOrganizationResult>>(
      `/organizations/${organizationId}/leave`,
      options
    );
    return response.data.data!;
  },

  // 서류 업로드 (사업자등록증 / 지정서 구분)
  uploadVerificationDocument: async (
    organizationId: string,
    file: File,
    type: VerificationDocumentType
  ): Promise<{ document: VerificationDocument; verificationStatus: VerificationStatus }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const response = await api.post<ApiResponse<{ document: VerificationDocument; verificationStatus: VerificationStatus }>>(
      `/organizations/${organizationId}/verification-documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data!;
  },

  /**
   * 검증 서류 다운로드 — **인증 전용 라우트**(2026-10-19 신설).
   *
   * ⚠️ `doc.url`을 `<a href>`에 그대로 넣지 말 것. 서버 인증이 Bearer 헤더 전용(쿠키 미지원)이라
   * 평범한 내비게이션은 **401**이고, 그래서 이 blob 경로가 **유일한 열람 수단**이다. 서버가 그 purpose를
   * `AUTH_ONLY`로 분류한 뒤(`files/file-public-visibility.ts`) 종전의 공개 URL은 더 내려오지 않는다.
   * 선례는 `dataExportApi.downloadFile`(같은 파일) — 그쪽도 인증이 필요한 파일이라 blob으로 받는다.
   */
  downloadVerificationDocument: async (
    organizationId: string,
    docId: string
  ): Promise<Blob> => {
    const response = await api.get(
      `/organizations/${organizationId}/verification-documents/${docId}`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  // 지정서 삭제
  deleteVerificationDocument: async (
    organizationId: string,
    docId: string
  ): Promise<void> => {
    await api.delete(`/organizations/${organizationId}/verification-documents/${docId}`);
  },

  // 외부 회사 멤버 생성 (결재자 등록용 — SiteContact 미생성)
  createExternalMember: async (
    companyId: string,
    data: { name: string; email?: string; phone?: string },
  ): Promise<{ id: string; name: string; phone: string | null; email: string | null }> => {
    const response = await api.post<ApiResponse<{ id: string; name: string; phone: string | null; email: string | null }>>(
      `/organizations/${companyId}/members/external`,
      data,
    );
    return response.data.data!;
  },

  /**
   * 계정 없는 인원의 **연락처 보정** (Phase F-G).
   *
   * F-F가 "계정도 연락처도 없는 사람"의 담당자 지정을 막으므로, 결재라인 편집에서 그 자리에
   * 채울 수 있어야 차단이 막다른 길이 되지 않는다. **계정 보유자는 서버가 400으로 거부**한다 —
   * 남의 계정 연락처는 본인이나 운영자가 바꾼다.
   */
  updateMemberContact: async (
    companyId: string,
    memberId: string,
    data: { phone: string; email?: string },
  ): Promise<{ id: string; name: string; phone: string | null; email: string | null }> => {
    const response = await api.patch<ApiResponse<{ id: string; name: string; phone: string | null; email: string | null }>>(
      `/organizations/${companyId}/members/${memberId}/contact`,
      data,
    );
    return response.data.data!;
  },
};

// Site API
export const sitesApi = {
  // 현장 목록 조회 (groupId 생략 시 접근 가능한 전체 그룹)
  findAll: async (companyId: string, groupId?: string | null): Promise<Site[]> => {
    const qs = groupId ? `?groupId=${encodeURIComponent(groupId)}` : '';
    const response = await api.get<ApiResponse<Site[]>>(
      `/organizations/${companyId}/sites${qs}`
    );
    return response.data.data!;
  },

  // 현장 조회
  findById: async (companyId: string, siteId: string): Promise<Site> => {
    const response = await api.get<ApiResponse<Site>>(
      `/organizations/${companyId}/sites/${siteId}`
    );
    return response.data.data!;
  },

  // 현장 생성 — P4 플래그가 켜진 조직이 남의 회사를 지정하면 현장이 아니라 «업체 승인 대기» 요청 응답이 온다
  // (`isCreateSiteRequested`로 가른다 — 호출부는 `useSiteFormState` 한 곳)
  create: async (companyId: string, data: CreateSiteDto): Promise<Site | CreateSiteRequestedResponse> => {
    const response = await api.post<ApiResponse<Site | CreateSiteRequestedResponse>>(
      `/organizations/${companyId}/sites`,
      data
    );
    return response.data.data!;
  },

  // 현장 수정
  update: async (companyId: string, siteId: string, data: UpdateSiteDto, options?: { silent?: boolean }): Promise<Site> => {
    const params = options?.silent ? '?silent=true' : '';
    const response = await api.patch<ApiResponse<Site>>(
      `/organizations/${companyId}/sites/${siteId}${params}`,
      data
    );
    return response.data.data!;
  },

  // 현장 삭제
  delete: async (companyId: string, siteId: string): Promise<void> => {
    await api.delete(`/organizations/${companyId}/sites/${siteId}`);
  },

  // 할당 멤버 조회
  getAssignments: async (companyId: string, siteId: string): Promise<SiteAssignment[]> => {
    const response = await api.get<ApiResponse<SiteAssignment[]>>(
      `/organizations/${companyId}/sites/${siteId}/assignments`
    );
    return response.data.data!;
  },

  // 멤버 할당
  assignMember: async (
    companyId: string,
    siteId: string,
    data: AssignMemberDto
  ): Promise<SiteAssignment> => {
    const response = await api.post<ApiResponse<SiteAssignment>>(
      `/organizations/${companyId}/sites/${siteId}/assignments`,
      data
    );
    return response.data.data!;
  },

  // 할당 해제
  unassignMember: async (
    companyId: string,
    siteId: string,
    memberId: string,
    options?: { transferToMemberId?: string; force?: boolean },
  ): Promise<void> => {
    const params = new URLSearchParams();
    if (options?.transferToMemberId) params.set('transferTo', options.transferToMemberId);
    if (options?.force) params.set('force', 'true');
    const qs = params.toString();
    await api.delete(
      `/organizations/${companyId}/sites/${siteId}/assignments/${memberId}${qs ? `?${qs}` : ''}`
    );
  },

  // 담당자 타입 변경 (정/부 담당자)
  updateAssignment: async (
    companyId: string,
    siteId: string,
    memberId: string,
    assignmentType: AssignmentType
  ): Promise<SiteAssignment> => {
    const response = await api.patch<ApiResponse<SiteAssignment>>(
      `/organizations/${companyId}/sites/${siteId}/assignments/${memberId}`,
      { assignmentType }
    );
    return response.data.data!;
  },

  // (현장별 멤버 권한 오버라이드 updateAssignmentPermissions — 3상태 canManageInspections — 는 P7-a에서 서버 컬럼과 함께 제거)

  // 현장 통계 조회
  getSiteStats: async (companyId: string, siteId: string): Promise<SiteStatsResponse> => {
    const response = await api.get<ApiResponse<SiteStatsResponse>>(
      `/organizations/${companyId}/sites/${siteId}/stats`
    );
    return response.data.data!;
  },

  // 위험요인 추이 조회
  getFindingsTrend: async (
    companyId: string,
    siteId: string,
    period: TrendPeriodType = 'daily'
  ): Promise<FindingsTrendResponse> => {
    const response = await api.get<ApiResponse<FindingsTrendResponse>>(
      `/organizations/${companyId}/sites/${siteId}/findings-trend`,
      { params: { period } }
    );
    return response.data.data!;
  },
};

// Invitations API (token-based)
export const invitationsApi = {
  // 토큰으로 초대 정보 조회 (공개)
  getByToken: async (token: string): Promise<OrganizationInvitation> => {
    const response = await api.get<ApiResponse<OrganizationInvitation>>(
      `/invitations/${token}`
    );
    return response.data.data!;
  },

  // 초대 수락 (로그인 필요)
  accept: async (token: string): Promise<OrganizationMember> => {
    const response = await api.post<ApiResponse<OrganizationMember>>(
      `/invitations/${token}/accept`
    );
    return response.data.data!;
  },

  // 초대 거절 (로그인 필요)
  reject: async (token: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<ApiResponse<{ success: boolean; message: string }>>(
      `/invitations/${token}/reject`
    );
    return response.data.data!;
  },

  // 내 대기 중인 초대 목록 조회 (로그인 필요)
  getMyInvitations: async (): Promise<OrganizationInvitation[]> => {
    const response = await api.get<ApiResponse<OrganizationInvitation[]>>(
      `/users/me/invitations`
    );
    return response.data.data || [];
  },
};

// 구 결재자 프로필(`ApproverProfile`) 타입·CRUD api·DTO는 approver-profile-retirement(E3 2026-09-16 서버
// 엔드포인트, E4 같은 날 shared 타입·DB 테이블)에서 전부 제거했다 — 결재자 등록은 멤버 초대·외부 인원
// 등록·결재라인 편집 모달로 일원화(2026-08-23 결정), 결재라인 행의 신원은 `memberId` 하나다.

// ==========================================
// Approval Line Types
// ==========================================

// 결재선 축 값(templateKey)의 타입은 `string`이다(현역 값은 양식 키 `report_templates.key`,
// 과거 값은 은퇴 유형 문자열) — 유형 union으로 좁히지 말 것.
// 화면 라벨은 양식 이름(`templateName`)으로 붙인다 — 축 값(키)을 라벨로 흘리지 않는다.

// ==========================================
// Approver Directory API — 「결재 관리」 화면(읽기 전용)
// ==========================================

/**
 * 결재자로 **실제로 쓰인** Member 목록(결재 서명 ∪ 현장 결재선 지정). 각 칸의 의미는 shared
 * `ApproverDirectoryEntry` JSDoc이 정본이고, 정렬(마지막 활동 내림차순)은 서버가 한다 — 화면은
 * 받은 순서를 그대로 그린다.
 */
export const approverDirectoryApi = {
  list: async (organizationId: string): Promise<ApproverDirectoryEntry[]> => {
    const response = await api.get<ApiResponse<ApproverDirectoryEntry[]>>(
      `/organizations/${organizationId}/approvers`
    );
    return response.data.data || [];
  },
};

// ==========================================
// Hazard Category Types
// ==========================================

// Re-export from @safetyhub/shared for backwards compatibility
export type { HazardCategory, OrganizationCategory, OrganizationType } from '@safetyhub/shared';

// ==========================================
// Hazard Category API
// ==========================================

export const hazardCategoriesApi = {
  // 위험 카테고리 목록 조회 (시스템 기본 + 커스텀)
  getAll: async (organizationId: string, siteId?: string): Promise<HazardCategory[]> => {
    const qs = siteId ? `?siteId=${siteId}` : '';
    const response = await api.get<ApiResponse<HazardCategory[]>>(
      `/organizations/${organizationId}/hazard-categories${qs}`
    );
    return response.data.data || [];
  },

  // 커스텀 위험 카테고리 생성
  create: async (organizationId: string, label: string): Promise<HazardCategory> => {
    const response = await api.post<ApiResponse<HazardCategory>>(
      `/organizations/${organizationId}/hazard-categories`,
      { label }
    );
    return response.data.data!;
  },
};

// Helper functions
// (유형 라벨 위임 함수를 두지 않는다 — 라벨의 입력은 양식 이름뿐이고, 소비처(Dashboard)는
//  `lib/reports`의 정본을 직접 부른다.)

export const CATEGORY_OPTIONS: Array<{ value: OrganizationCategory; label: string }> = [
  { value: 'WORKPLACE', label: '일반 사업장' },
  { value: 'GUIDANCE_AGENCY', label: '건설재해예방기술지도기관' },
  { value: 'SAFETY_MANAGEMENT_AGENCY', label: '안전관리전문기관' },
  { value: 'WORKSPACE', label: '개인/팀' },
];

// 표시 종류 판정(`memberKindOf`)과 종류→라벨 맵(`MEMBER_KIND_LABELS`)은 shared가 소유한다 —
// 이 파일 머리에서 import해 함께 내보낸다(admin과 같은 판정을 쓰기 위해).

/** 종류 → 라벨. `null`(외부 인원) 문구만 web 것이다 — admin은 「외부 연락처」라 부른다. */
export function getMemberKindLabel(kind: MemberKind | null): string {
  return kind ? MEMBER_KIND_LABELS[kind] : EXTERNAL_LABEL;
}

/**
 * 종류 → 공용 Badge tone 매핑 (UI 리스킨 목업 `.role` 톤 — 소유자 violet · 구성원 blue · 외부 slate).
 * 기존 RoleBadge 컴포넌트(모바일 등 공유)와 새 Badge(@safetyhub/ui) 소비처가 같은 매핑을 쓴다.
 */
export function getMemberKindBadgeTone(kind: MemberKind | null): BadgeTone {
  if (kind === 'owner') return 'violet';
  if (kind === 'member') return 'blue';
  return 'slate';
}

/**
 * 멤버 행이 화면에 내보일 역할 배지들(P7-b-2).
 *
 * 서버가 실은 **실제 역할 이름 전부**를 그대로 쓴다 — 겸직이 정상이라 배열이고, 2종으로 뭉치면 「안전관리자」와
 * 「관리감독자」가 같은 「구성원」 배지가 되어 목록에서 구분이 사라진다. 배정이 없으면(= 외부 인원) 종류 라벨
 * 하나로 떨어진다. 옛 형상(서버가 `roles`를 안 싣던 응답)도 같은 자리로 떨어져 화면이 비지 않는다.
 *
 * tone은 소유자 역할만 violet, 나머지 배정 역할은 blue, 배정 없음은 slate — 배지·분포 바가 같은 말을 한다.
 * 소유자 판정은 여기서 파생하지 않고 `member.isOwner`를 읽는다(서버가 오버라이드까지 보고 정한 값).
 */
export function memberRoleBadges(
  member: { isOwner?: boolean; countsAsMember?: boolean; roles?: { key: string; name: string }[] },
): Array<{ key: string; label: string; tone: BadgeTone }> {
  const roles = member.roles ?? [];
  if (roles.length === 0) {
    const kind = memberKindOf(member);
    return [{ key: kind ?? 'external', label: getMemberKindLabel(kind), tone: getMemberKindBadgeTone(kind) }];
  }
  return roles.map((r) => ({
    key: r.key,
    label: r.name,
    tone: r.key === OWNER_ROLE_KEY && member.isOwner === true ? 'violet' : 'blue',
  }));
}

/** 초대가 소유자 초대인가 — `roleKeys`에 시스템 소유자 역할 key가 실렸는가(P7-b-2, 옛 `role === 'OWNER'`) */
export function isOwnerInvitation(invitation: { roleKeys?: string[] | null }): boolean {
  return (invitation.roleKeys ?? []).includes(OWNER_ROLE_KEY);
}

/** 초대 배지 라벨 — 소유자 초대면 「조직소유자」, 아니면 「구성원」(구체 역할 이름은 초대 상세·역할 화면이 맡는다) */
export function getInvitationKindLabel(invitation: { roleKeys?: string[] | null }): string {
  return isOwnerInvitation(invitation) ? OWNER_LABEL : MEMBER_LABEL;
}

/**
 * 초대 상태 → 공용 Badge tone 매핑 (목업 `.mst` 톤: 대기 amber · 만료/거절 rose·slate).
 */
export function getInvitationStatusBadgeTone(status: InvitationStatus): BadgeTone {
  const tones: Record<InvitationStatus, BadgeTone> = {
    PENDING: 'amber',
    ACCEPTED: 'emerald',
    REJECTED: 'rose',
    EXPIRED: 'slate',
    CANCELLED: 'slate',
  };
  return tones[status] || 'slate';
}

/**
 * 현장 생애주기 라벨 — 저장된 `Site.status` 하나가 정본이다.
 * 점검/보고서 진행 상태는 다른 축이라 여기 들어오지 않는다(결정 기록 `2026-08-03-site-status-axes`).
 */
export const SITE_LIFECYCLE_LABELS: Record<SiteStatus, string> = {
  ACTIVE: '운영중',
  PAUSED: '일시중지',
  CLOSED: '종료',
  TRANSFER_PENDING: '이관중',
};

/**
 * 생애주기 → 공용 Badge tone. 운영중만 emerald고, 멈춘 상태는 amber(재개 가능)/slate(끝남)로
 * 갈라 둔다 — 둘을 한 색으로 접으면 boolean 시절과 같은 정보 손실이 된다.
 * 이관중은 이관 플로우 전용 상태라 다른 계열(violet)로 눈에 띄게 둔다.
 */
export function getSiteLifecycleBadgeTone(status: SiteStatus): BadgeTone {
  const tones: Record<SiteStatus, BadgeTone> = {
    ACTIVE: 'emerald',
    PAUSED: 'amber',
    CLOSED: 'slate',
    TRANSFER_PENDING: 'violet',
  };
  return tones[status] || 'slate';
}

/**
 * 목록에 **띄울** 현장인가 — 일시중지·종료만 걷어낸다.
 *
 * 이관중(`TRANSFER_PENDING`)이 여기서 살아남는 건 의도다: 이관 대기 중인 현장은 곧 남의
 * 것이 되지만 아직 조직의 현장이라, 목록에서 통째로 사라지면 "내 현장이 없어졌다"로 읽힌다.
 * 대신 고를 수는 없어야 하므로, 선택 가능 여부는 아래 `isSiteWritable`이 따로 답한다.
 */
export function isSiteSelectable(site: { status: SiteStatus }): boolean {
  return site.status !== 'PAUSED' && site.status !== 'CLOSED';
}

/**
 * 점검·보고서를 새로 **만들 수 있는** 현장인가 — 이관중은 이관 플로우가 소유 중이라 제외한다.
 *
 * `isSiteSelectable`이 통과시킨 이관중 현장을 그대로 고르게 두면 서버가 거절해, 사용자에게는
 * 고를 수 있게 생긴 항목을 눌렀더니 실패하는 화면이 된다. 목록에는 남기되 비활성으로
 * 표시하는 게 정본이다(`CreateInspectionModal`의 `(이관대기)` 항목).
 */
export function isSiteWritable(site: { status: SiteStatus }): boolean {
  return isSiteSelectable(site) && site.status !== 'TRANSFER_PENDING';
}

/**
 * 현장 수정 폼에서 고를 수 있는 상태 — `TRANSFER_PENDING`은 이관 플로우가 소유하는 상태라
 * 직접 설정할 수 없다(서버 `UpdateSiteDto`도 같은 3값만 받는다).
 */
export type SiteEditableStatus = Exclude<SiteStatus, 'TRANSFER_PENDING'>;

export const SITE_EDITABLE_STATUS_OPTIONS: { value: SiteEditableStatus; label: string }[] = [
  { value: 'ACTIVE', label: SITE_LIFECYCLE_LABELS.ACTIVE },
  { value: 'PAUSED', label: SITE_LIFECYCLE_LABELS.PAUSED },
  { value: 'CLOSED', label: SITE_LIFECYCLE_LABELS.CLOSED },
];

/** 이관중 현장은 폼이 status를 대신 써 보내면 안 된다(이관 플로우가 소유 중). */
export function isSiteStatusEditable(status: SiteStatus): status is SiteEditableStatus {
  return status !== 'TRANSFER_PENDING';
}

// ==========================================
// Data Export Types
// ==========================================

export type DownloadMethod = 'direct' | 'sms' | 'email';
export type ExportScope = 'FULL_ORG' | 'MY_DATA';
export type ExportStatus = 'GENERATING' | 'READY' | 'DOWNLOADED' | 'EXPIRED' | 'FAILED' | 'DELETED';

export interface OrganizationExportResult {
  exportId: string;
  status: ExportStatus;
  scope: ExportScope;
  message: string;
}

/**
 * 전체 데이터 내보내기에서 **한 조직이 어떤 범위로 담기는가**.
 *
 * scope가 내보내기 한 건이 아니라 **조직마다** 붙는다 — 같은 사람이 A사에서는 OWNER,
 * B사에서는 멤버일 수 있기 때문이다(결정 기록 2026-08-22 E3).
 */
export interface UserExportScope {
  companyId: string;
  organizationName: string;
  scope: ExportScope;
}

export interface UserExportResult {
  exportId: string;
  status: ExportStatus;
  organizations: UserExportScope[];
  /** 이미 준비 중이던 건을 그대로 돌려받은 경우 true (중복 요청 방지) */
  reused: boolean;
  message: string;
}

export interface ExportStatusResponse {
  id: string;
  status: ExportStatus;
  downloadMethod: string;
  inspectionCount: number;
  reportCount: number;
  tbmCount: number;
  photoCount: number;
  downloadCount: number;
  maxDownloads: number;
  expiresAt: string;
  canDownload: boolean;
}

// ==========================================
// Data Export API
// ==========================================

export const dataExportApi = {
  // 조직 데이터 내보내기 요청
  // ⚠️ 수신처(phone/email)는 보내지 않는다 — 서버가 계정에서 파생하고, body에 실으면
  // `forbidNonWhitelisted`가 400으로 거부한다 (S27 수신자 축, 2026-08-22).
  createOrganizationExport: async (
    companyId: string,
    downloadMethod: DownloadMethod
  ): Promise<OrganizationExportResult> => {
    const response = await api.post<ApiResponse<OrganizationExportResult>>(
      `/data-export/organizations/${companyId}`,
      { downloadMethod }
    );
    return response.data.data!;
  },

  // 전체 데이터 내보내기 — 내가 속한 **모든 활성 조직**을 한 벌로 (결정 기록 2026-08-22).
  //
  // 예전에 이 자리에 있던 createUserExport는 서버에 라우트가 없어 한 번도 동작한 적이 없었고
  // 2026-08-22에 버튼째 걷어냈다. 그때 드러난 것이 **비OWNER는 탈퇴하지 않고서는 자기
  // 데이터를 받을 방법이 없다**는 공백이라(조직 설정 화면이 비OWNER를 리다이렉트한다),
  // 라우트를 만들고 다시 붙였다.
  getUserExportScopes: async (): Promise<{
    organizations: UserExportScope[];
    canExport: boolean;
  }> => {
    const response = await api.get<
      ApiResponse<{ organizations: UserExportScope[]; canExport: boolean }>
    >('/data-export/user/scopes');
    return response.data.data!;
  },

  // ⚠️ 수신처(phone/email)는 보내지 않는다 — 서버가 계정에서 파생하고, body에 실으면
  // `forbidNonWhitelisted`가 400으로 거부한다 (S27 수신자 축).
  createUserExport: async (downloadMethod: DownloadMethod): Promise<UserExportResult> => {
    const response = await api.post<ApiResponse<UserExportResult>>('/data-export/user', {
      downloadMethod,
    });
    return response.data.data!;
  },

  // 내보내기 상태 조회
  getExportStatus: async (exportId: string): Promise<ExportStatusResponse> => {
    const response = await api.get<ApiResponse<ExportStatusResponse>>(
      `/data-export/${exportId}`
    );
    return response.data.data!;
  },

  // 파일 다운로드 (인증 포함)
  downloadFile: async (exportId: string): Promise<Blob> => {
    const response = await api.get(`/data-export/${exportId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// 대시보드 통계 타입
export interface DashboardStats {
  sitesCount: number;
  inProgressInspections: number;
  completedInspections: number;
  scheduledInspections: number;
  pendingReports: number;
  approvedReports: number;
  unresolvedFindings: number;
  resolvedFindings: number;
  /** 양식(templateKey)별 보고서 건수 — 추이(`DashboardTrendData.reports`)와 같은 축·같은 이름 해소. */
  reportTypeStats: { templateKey: string | null; templateName: string | null; count: number }[];
}

// 대시보드 통계 API
export async function getDashboardStats(
  organizationId: string,
  dateFrom?: string,
  dateTo?: string,
  groupId?: string | null,
): Promise<DashboardStats> {
  const params = new URLSearchParams();
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  if (groupId) params.set('groupId', groupId);
  const qs = params.toString();
  const response = await api.get<ApiResponse<DashboardStats>>(
    `/organizations/${organizationId}/dashboard-stats${qs ? `?${qs}` : ''}`
  );
  return response.data.data!;
}

// 대시보드 일별 추이 API
export interface DashboardTrendData {
  inspections: { date: string; count: number }[];
  /**
   * 일별 보고서 건수 — 양식(`templateKey`/`templateName` = 실제 유형)별 행. 목록·배지와 같은
   * 규약(`getReportTypeLabel(templateName)`)으로 계열/라벨을 만든다. 응답에 저장용 `type` 필드는
   * 없다. 옛 서버 응답 호환을 위해 두 필드는 옵셔널.
   */
  reports: {
    date: string;
    count: number;
    templateKey?: string | null;
    templateName?: string | null;
  }[];
  resolvedFindings: { date: string; count: number }[];
  unresolvedFindings: { date: string; count: number }[];
}

export async function getDashboardTrend(
  organizationId: string,
  dateFrom: string,
  dateTo: string,
  groupId?: string | null,
): Promise<DashboardTrendData> {
  const params = new URLSearchParams({ dateFrom, dateTo });
  if (groupId) params.set('groupId', groupId);
  const response = await api.get<ApiResponse<DashboardTrendData>>(
    `/organizations/${organizationId}/dashboard-trend?${params.toString()}`
  );
  return response.data.data!;
}

export { extractApiError };

// ==========================================
// 현장 결재선 메타 API (assignmentChain 기반) — `approvalLineMetaApi`·`/approval-line-meta/:templateKey`
// ==========================================

/**
 * GET /api/v1/sites/:siteId/approval-line-meta 응답 data.
 * 축 값(`templateKey`)은 Composer 템플릿 key. 마지막 층(구조·한도·활성 여부)은 응답에 없고 화면이 템플릿 목록
 * (`listEnabledTemplates`)의 `approvalLineMeta`에서 파생한다 — system 층·조직 override 층이 없다.
 *
 * 행 타입은 shared `SiteApprovalLineMetaData`(서버 `ApprovalLineMetaService.getMeta`·admin 카드와 같은 계약)를
 * 그대로 쓴다 — web 로컬 복제를 두지 말 것.
 */
export interface SiteApprovalLineMetaResponse {
  siteOverrides: SiteApprovalLineMetaData[];
}

/**
 * PATCH /api/v1/sites/:siteId/approval-line-meta/:templateKey body.
 *
 * `maxAdditionalLines`·`hasApprovalLinePolicy`는 보내지 않는다 — 둘 다 양식(템플릿 `approvalLineMeta`) 단독
 * 권위 값이라 현장 override 컬럼이 2026-09-16 R1에서 DROP됐고, 서버 DTO가 whitelist라 실어 보내면 400이다.
 * 조회 응답(`SiteApprovalLineMetaData`)의 같은 두 필드는 서버가 양식 값으로 채워 주는 **해소 결과**라 읽기는 그대로다.
 */
export interface UpdateSiteApprovalLineMetaDto {
  lines: ApprovalLineMetaRow[];
  reason: string;
}

/**
 * 결재선 메타 세 호출이 싣는 `?companyId` — **현재 작업공간**(`authStore.currentCompanyId`, 정본은
 * `lib/feedback.ts`의 같은 읽기). 서버는 그 조직이 현장의 작업 조직이 아니면 403, 지목하면 그 조직의
 * 메타를 준다(리뷰 1103 T7). 다중 소속자가 보던 조직과 다른 조직의 메타를 고치지 않게 한다.
 * ⚠️ 값이 없으면(조직 없는 사용자 등) **키를 싣지 않는다** — 빈 문자열은 서버에서 다른 뜻이 된다.
 * (`stores/auth → lib/auth·lib/api`는 단방향이라 이 import는 순환을 만들지 않는다.)
 */
function currentCompanyParams(): { params: { companyId: string } } | undefined {
  const companyId = useAuthStore.getState().currentCompanyId;
  return companyId ? { params: { companyId } } : undefined;
}

export const approvalLineMetaApi = {
  /** 현장 결재라인 메타 (siteOverrides) 조회 */
  getSiteMeta: async (siteId: string): Promise<SiteApprovalLineMetaResponse> => {
    const response = await api.get<ApiResponse<SiteApprovalLineMetaResponse>>(
      `/sites/${siteId}/approval-line-meta`,
      currentCompanyParams(),
    );
    return response.data.data!;
  },

  /** 현장 결재라인 메타 특정 templateKey override 저장 */
  updateSiteMeta: async (
    siteId: string,
    templateKey: string,
    dto: UpdateSiteApprovalLineMetaDto,
  ): Promise<void> => {
    await api.patch(
      `/sites/${siteId}/approval-line-meta/${templateKey}`,
      dto,
      currentCompanyParams(),
    );
  },

  /** 현장 결재라인 메타 override 삭제 → 양식(템플릿) 기본값 상속으로 되돌리기 */
  deleteSiteMeta: async (siteId: string, templateKey: string): Promise<void> => {
    await api.delete(
      `/sites/${siteId}/approval-line-meta/${templateKey}`,
      currentCompanyParams(),
    );
  },
};

// ==========================================
// Site Contacts API (assignmentChain용 외부 담당자)
// ==========================================

export interface SiteContact {
  id: string;
  siteId: string;
  memberId: string;
  name: string;
  phone: string;
  email?: string | null;
  department?: string | null;
  position?: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface CreateSiteContactBody {
  name: string;
  phone: string;
  email?: string;
  department?: string;
  position?: string;
}

export const siteContactsApi = {
  /** 현장 담당자 목록 */
  findAll: async (companyId: string, siteId: string): Promise<SiteContact[]> => {
    const response = await api.get<ApiResponse<SiteContact[]>>(
      `/organizations/${companyId}/sites/${siteId}/contacts`,
    );
    return response.data.data || [];
  },

  /** 외부 담당자 inline 생성 → memberId 반환 */
  create: async (
    companyId: string,
    siteId: string,
    dto: CreateSiteContactBody,
  ): Promise<SiteContact> => {
    const response = await api.post<ApiResponse<SiteContact>>(
      `/organizations/${companyId}/sites/${siteId}/contacts`,
      dto,
    );
    return response.data.data!;
  },
};
